import React, { useEffect, useRef } from 'react';
import { HyperlinkRedirectStepComponent } from 'api/types';
import MessageContainer from 'components/MessageContainer';
import AgathaMessage from 'components/AgathaMessage/AgathaMessage';
import { useAppSelector } from 'hooks';
import { selectIsCurrentTabPrimary } from 'persistence/persistenceBetweenTabs';
import { selectIsAuthorIconVisible } from 'reducers/workflows';

type HyperlinkRedirectStepProps = {
  isLastStep: boolean;
  setStepSent: (query?: string) => void;
  stepIndex: number;
  widgetComponent: HyperlinkRedirectStepComponent;
};

const HyperlinkRedirectStep: React.FC<
  React.PropsWithChildren<HyperlinkRedirectStepProps>
> = ({ setStepSent, stepIndex, widgetComponent }) => {
  const { isSent } = widgetComponent;
  const { destination_link, forethought_text, redirect_option } =
    widgetComponent.component_fields;
  const hiddenLinkRef = useRef<HTMLAnchorElement>(null);
  let linkRel = '';
  let linkTarget = '';
  const isCurrentTabPrimary = useAppSelector(selectIsCurrentTabPrimary);
  const isAuthorIconVisible = useAppSelector(state =>
    selectIsAuthorIconVisible(state, stepIndex),
  );

  switch (redirect_option) {
    case 'current_tab':
      linkRel = 'noopener';
      linkTarget = '_parent';
      break;
    case 'new_tab':
      linkRel = 'noopener';
      linkTarget = '_blank';
      break;
    default:
      linkRel = 'noopener';
      linkTarget = '_blank';
  }

  useEffect(() => {
    if (!isSent && hiddenLinkRef.current) {
      if (isCurrentTabPrimary) {
        hiddenLinkRef.current.click();
      }
      setStepSent();
    }
  }, [isCurrentTabPrimary, isSent, setStepSent]);

  return (
    <MessageContainer>
      <AgathaMessage
        isAuthorIconVisible={isAuthorIconVisible}
        stepIndex={stepIndex}
      >
        <>
          {forethought_text.redirecting}
          <br />
          <br />
          {forethought_text.redirect_message}{' '}
          <a
            aria-label={
              linkTarget === '_blank'
                ? 'click here (opens in a new tab)'
                : undefined
            }
            href={decodeURIComponent(destination_link)}
            ref={hiddenLinkRef}
            rel={linkRel}
            target={linkTarget}
          >
            {forethought_text.click_here}
          </a>
        </>
      </AgathaMessage>
    </MessageContainer>
  );
};

export default HyperlinkRedirectStep;
